import { forwardRef, memo, useCallback, useEffect, useId, useState } from 'react';
import { cva, VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/styles';
import { inputStyles } from '../Input/Input';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';

import { HexagonColorPicker } from './HexagonColorPicker';
import { tryCompleteHex } from './hexagonColorPicker.utils';

const colorInputTriggerVariants = cva(
  'size-10 rounded-sm border transition-all outline-none focus-visible:ring-1 focus-visible:ring-border-input-select focus-visible:ring-offset-1',
  {
    variants: {
      disabled: {
        true: 'cursor-not-allowed',
        false: '',
      },
      size: {
        sm: 'size-8',
        md: 'size-10',
      },
      readOnly: {
        true: 'cursor-default',
        false: '',
      },
      open: {
        true: '!border-border-input-select',
        false: '',
      },
    },
    compoundVariants: [
      {
        disabled: false,
        readOnly: false,
        className: 'cursor-pointer hover:opacity-75',
      },
    ],
    defaultVariants: {
      size: 'md',
      disabled: false,
      readOnly: false,
      open: false,
    },
  },
);

/** 6-digit #rrggbb for pickers and `input type="color"` (falls back when value is missing or invalid). */
function normalizeHexString(value: string | undefined, fallbackHex: string): string {
  const trimmed = value?.trim() ?? '';
  const six = trimmed.match(/^#([0-9a-fA-F]{6})$/);
  if (six) {
    return `#${six[1].toLowerCase()}`;
  }
  const three = trimmed.match(/^#([0-9a-fA-F]{3})$/);
  if (three) {
    const [r, g, b] = three[1];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  const fb = fallbackHex.match(/^#([0-9a-fA-F]{6})$/i);
  return fb ? `#${fb[1].toLowerCase()}` : '#808080';
}

export type ColorInputProps = VariantProps<typeof colorInputTriggerVariants> &
  Omit<React.ComponentProps<'input'>, 'value' | 'onChange'> & {
    value?: string;
    onChange?: (color: string) => void;
    /**
     * `swatches` — grid of preset colors (default).
     * `picker` — honeycomb of hex tiles inside a clip-path hex (W3Schools-style discrete map) and a hex field.
     */
    variant?: 'swatches' | 'picker';
    /** Used with `variant="picker"` when `value` is not a parseable hex. */
    pickerFallbackHex?: string;
    swatches?: string[];
    swatchesPerRow?: number;
    closeOnColorSwatchClick?: boolean;
    /** When `variant` is `swatches`, adds a native OS color control below the grid. */
    allowArbitraryHex?: boolean;
    withinPortal?: boolean;
    containerClassName?: string;
    swatchClassName?: string;
    popoverContentClassName?: string;
  };

const ColorInputBase = (
  {
    value,
    onChange,
    variant = 'swatches',
    pickerFallbackHex = '#808080',
    swatches = [],
    swatchesPerRow = 5,
    closeOnColorSwatchClick = true,
    allowArbitraryHex = false,
    withinPortal = true,
    disabled,
    readOnly,
    size,
    className,
    containerClassName,
    swatchClassName,
    popoverContentClassName,

    ...rest
  }: ColorInputProps,
  ref: React.ForwardedRef<HTMLInputElement>,
) => {
  const [open, setOpen] = useState(false);
  const nativePickerId = useId();
  const hexFieldId = `${nativePickerId}-hex`;
  const isPickerVariant = variant === 'picker';
  const nativePickerFallback = swatches[0] ?? '#808080';
  const nativePickerValue = normalizeHexString(value, nativePickerFallback);
  const pickerResolvedHex = normalizeHexString(value, pickerFallbackHex);

  const [hexDraft, setHexDraft] = useState(pickerResolvedHex);
  useEffect(() => {
    if (isPickerVariant && open) {
      setHexDraft(pickerResolvedHex);
    }
  }, [isPickerVariant, open, pickerResolvedHex]);

  const showPopover = isPickerVariant || swatches.length > 0 || allowArbitraryHex;

  const handleSwatchClick = useCallback(
    (color: string) => {
      onChange?.(color);
      if (closeOnColorSwatchClick) {
        setOpen(false);
      }
    },
    [onChange, closeOnColorSwatchClick],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!readOnly && !disabled) {
        onChange?.(e.target.value);
      }
    },
    [onChange, readOnly, disabled],
  );

  const handleNativeColorChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!readOnly && !disabled) {
        onChange?.(e.target.value);
        if (closeOnColorSwatchClick) {
          setOpen(false);
        }
      }
    },
    [onChange, readOnly, disabled, closeOnColorSwatchClick],
  );

  const handlePickerColorChange = useCallback(
    (next: string) => {
      if (!readOnly && !disabled) {
        onChange?.(next);
      }
    },
    [onChange, readOnly, disabled],
  );

  const handleHexFieldChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (readOnly || disabled) return;
      const raw = e.target.value;
      setHexDraft(raw);
      const complete = tryCompleteHex(raw);
      if (complete) {
        onChange?.(complete);
      }
    },
    [onChange, readOnly, disabled],
  );

  return (
    <div className={cn('relative inline-flex items-center', containerClassName)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              colorInputTriggerVariants({
                disabled: !!disabled,
                readOnly: !!readOnly,
                open,
                size,
              }),
              className,
            )}
            style={{ backgroundColor: value, borderColor: value }}
            disabled={disabled || readOnly}
            aria-label="Select color"
          />
        </PopoverTrigger>

        <PopoverContent
          withinPortal={withinPortal}
          className={cn(
            'w-auto p-3',
            !showPopover && 'hidden',
            isPickerVariant && 'max-w-[min(100vw-2rem,280px)]',
            popoverContentClassName,
          )}
          align="start"
        >
          {isPickerVariant ? (
            <div className="flex flex-col gap-3">
              <HexagonColorPicker
                color={pickerResolvedHex}
                onChange={handlePickerColorChange}
                disabled={disabled || readOnly}
              />
              <div className="flex flex-col gap-1.5">
                <label htmlFor={hexFieldId} className="font-body-sm text-text-secondary">
                  Hex
                </label>
                <input
                  id={hexFieldId}
                  type="text"
                  value={hexDraft}
                  onChange={handleHexFieldChange}
                  disabled={disabled}
                  readOnly={readOnly}
                  spellCheck={false}
                  autoCapitalize="off"
                  autoCorrect="off"
                  className={cn(inputStyles({ variant: 'default', size: 'md', showFocusOutline: true }), 'font-mono')}
                  aria-label="Hex color"
                />
              </div>
            </div>
          ) : null}

          {!isPickerVariant && swatches.length > 0 ? (
            <div
              className="grid gap-2"
              style={{
                gridTemplateColumns: `repeat(${swatchesPerRow}, minmax(0, 1fr))`,
              }}
            >
              {swatches.map((swatch) => (
                <button
                  key={swatch}
                  type="button"
                  className={cn(
                    'size-10 cursor-pointer rounded-sm border transition-all focus:outline-none hover:opacity-75',
                    value === swatch && 'border-border-input-select!',
                    swatchClassName,
                  )}
                  style={{ backgroundColor: swatch, borderColor: swatch }}
                  onClick={() => handleSwatchClick(swatch)}
                  aria-label={`Select color ${swatch}`}
                />
              ))}
            </div>
          ) : null}

          {!isPickerVariant && allowArbitraryHex ? (
            <div className={cn('flex flex-col gap-2', swatches.length > 0 && 'mt-3 border-t border-border pt-3')}>
              <label htmlFor={nativePickerId} className="font-body-sm text-text-secondary">
                Custom color
              </label>
              <input
                id={nativePickerId}
                type="color"
                className="h-10 w-full min-w-40 cursor-pointer rounded-sm border border-border bg-transparent p-0.5 disabled:cursor-not-allowed"
                value={nativePickerValue}
                onChange={handleNativeColorChange}
                disabled={disabled || readOnly}
              />
            </div>
          ) : null}
        </PopoverContent>
      </Popover>

      {/* Hidden text value holder for forms; still in the a11y tree, so it needs a name. */}
      <input
        ref={ref}
        type="text"
        value={value}
        onChange={handleInputChange}
        disabled={disabled}
        readOnly={readOnly}
        className="sr-only"
        aria-label="Color value"
        {...rest}
      />
    </div>
  );
};

export const ColorInput = memo(forwardRef(ColorInputBase));
